import { SameGtin } from "@/components/SameGtin";
import * as Dialog from "@radix-ui/react-dialog";
import { PackUnitsSection } from "@/components/PackUnitsSection";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useQuery } from "@tanstack/react-query";
import { CONFIRMABLE_RULES, validateProduct, type Product, type ValidationResult } from "@logiful/domain";
import { ArrowRight, Check, CheckCircle2, ChevronDown, CircleDot, Clock, Lock, LockOpen, RefreshCw, Send, ShieldCheck, UploadCloud, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { toast } from "sonner";
import { Link } from "react-router";
import { api, ApiError, type ProductItem, type ProductPatch } from "@/lib/api";
import { fmtDateTime, fmtNumber, fmtRelative, ruleLabel } from "@/lib/format";
import { useTenant } from "./AppShell";
import { BestBeforeSection } from "./BestBeforeSection";
import { BundleEditor } from "./BundleEditor";
import { ChannelLogo } from "./ChannelLogo";
import { Badge, Button, cx, Input, Kbd, ScoreBar, SeverityIcon, Skeleton, Tip, ValidationBadge } from "./ui";
import { ProductFbaSection } from "@/components/FbaInfo";
import { intlLocale, t, msg } from "@/lib/i18n";

import { serverText } from "@/lib/server-text";
type Form = {
  name: string;
  gtin: string;
  mpn: string;
  manufacturer: string;
  grossWeightKg: string;
  netWeightKg: string;
  lengthCm: string;
  widthCm: string;
  heightCm: string;
  hsCode: string;
  countryOfOrigin: string;
  batchManaged: boolean;
  bestBeforeManaged: boolean;
  serialManaged: boolean;
  fragile: boolean;
  dangerous: boolean;
  unNumber: string;
  hazardClass: string;
  limitedQuantity: boolean;
};

/** "un 3481", "3481", "UN-3481" → "UN3481"; Unbekanntes bleibt sichtbar stehen (Prüfung meldet es) */
const normalizeUn = (s: string) => {
  const t = s.trim().toUpperCase();
  const digits = t.replace(/^UN[\s-]*/, "").replace(/\s+/g, "");
  return /^\d{4}$/.test(digits) ? `UN${digits}` : t;
};

const num = (s: string) => (s.trim() === "" ? undefined : Number(s.replace(",", ".")));
const str = (n: number | undefined) => (n == null ? "" : String(n).replace(".", ","));

function toForm(p: ProductItem): Form {
  return {
    name: p.name,
    gtin: p.identifiers.gtin ?? "",
    mpn: p.identifiers.mpn ?? "",
    manufacturer: p.identifiers.manufacturer ?? "",
    grossWeightKg: str(p.weight?.grossKg),
    netWeightKg: str(p.weight?.netKg),
    lengthCm: str(p.dimensions?.lengthCm),
    widthCm: str(p.dimensions?.widthCm),
    heightCm: str(p.dimensions?.heightCm),
    hsCode: p.customs.hsCode ?? "",
    countryOfOrigin: p.customs.countryOfOrigin ?? "",
    batchManaged: p.handling.batchManaged,
    bestBeforeManaged: p.handling.bestBeforeManaged,
    serialManaged: p.handling.serialManaged,
    fragile: p.handling.fragile ?? false,
    dangerous: !!p.handling.dangerousGoods,
    unNumber: p.handling.dangerousGoods?.unNumber ?? "",
    hazardClass: p.handling.dangerousGoods?.hazardClass ?? "",
    limitedQuantity: p.handling.dangerousGoods?.limitedQuantity ?? false,
  };
}

const dangerousOf = (f: Form) =>
  f.dangerous ? { unNumber: normalizeUn(f.unNumber), ...(f.hazardClass.trim() ? { hazardClass: f.hazardClass.trim() } : {}), ...(f.limitedQuantity ? { limitedQuantity: true } : {}) } : undefined;

/** Formular → Domain-Produkt für die Live-Prüfung (gleiche Regeln wie Server) */
function applyForm(p: ProductItem, f: Form): Product {
  const l = num(f.lengthCm), w = num(f.widthCm), h = num(f.heightCm);
  return {
    ...p,
    name: f.name,
    identifiers: { ...p.identifiers, gtin: f.gtin.trim() || undefined, mpn: f.mpn || undefined, manufacturer: f.manufacturer || undefined },
    weight: num(f.grossWeightKg) || num(f.netWeightKg) ? { grossKg: num(f.grossWeightKg), netKg: num(f.netWeightKg) } : undefined,
    dimensions: l || w || h ? { lengthCm: l ?? 0, widthCm: w ?? 0, heightCm: h ?? 0 } : undefined,
    customs: { ...p.customs, hsCode: f.hsCode.trim() || undefined, countryOfOrigin: f.countryOfOrigin.trim().toUpperCase() || undefined },
    handling: { ...p.handling, batchManaged: f.batchManaged, bestBeforeManaged: f.bestBeforeManaged, serialManaged: f.serialManaged, fragile: f.fragile, dangerousGoods: dangerousOf(f) },
  };
}

function diff(a: Form, b: Form): ProductPatch {
  const out: ProductPatch = {};
  const nullableStr = (v: string) => v.trim() || null;
  const nullableNum = (v: string) => num(v) ?? null;
  if (a.name !== b.name) out.name = b.name;
  if (a.gtin !== b.gtin) out.gtin = nullableStr(b.gtin);
  if (a.mpn !== b.mpn) out.mpn = nullableStr(b.mpn);
  if (a.manufacturer !== b.manufacturer) out.manufacturer = nullableStr(b.manufacturer);
  if (a.grossWeightKg !== b.grossWeightKg) { out.grossWeightKg = nullableNum(b.grossWeightKg); out.netWeightKg = nullableNum(b.grossWeightKg); }
  if (a.netWeightKg !== b.netWeightKg) out.netWeightKg = nullableNum(b.netWeightKg);
  if (a.lengthCm !== b.lengthCm) out.lengthCm = nullableNum(b.lengthCm);
  if (a.widthCm !== b.widthCm) out.widthCm = nullableNum(b.widthCm);
  if (a.heightCm !== b.heightCm) out.heightCm = nullableNum(b.heightCm);
  if (a.hsCode !== b.hsCode) out.hsCode = nullableStr(b.hsCode);
  if (a.countryOfOrigin !== b.countryOfOrigin) out.countryOfOrigin = nullableStr(b.countryOfOrigin.toUpperCase());
  for (const k of ["batchManaged", "bestBeforeManaged", "serialManaged", "fragile"] as const) if (a[k] !== b[k]) out[k] = b[k];
  if (JSON.stringify(dangerousOf(a) ?? null) !== JSON.stringify(dangerousOf(b) ?? null)) out.dangerousGoods = dangerousOf(b) ?? null;
  return out;
}

/** Prüf-Feldpfad → Formularfeld (für "Beheben" → Fokus) */
/** Felder, die der Shopify-Abgleich füllt und die geschützt werden können (wie FIELD_COLUMNS im Server) */
const LOCKABLE = new Set(["name", "description", "identifiers.gtin", "identifiers.mpn", "identifiers.manufacturer", "dimensions", "weight.grossKg", "weight.netKg", "customs.hsCode", "customs.countryOfOrigin", "customs.customsValue", "netRetailPrice"]);

const FIELD_TARGET: Record<string, keyof Form> = {
  "identifiers.gtin": "gtin",
  "weight.grossKg": "grossWeightKg",
  "weight.netKg": "netWeightKg",
  dimensions: "lengthCm",
  "identifiers.mpn": "mpn",
  "identifiers.manufacturer": "manufacturer",
  "customs.hsCode": "hsCode",
  "customs.countryOfOrigin": "countryOfOrigin",
  "handling.dangerousGoods.unNumber": "unNumber",
  "handling.dangerousGoods": "unNumber",
  name: "name",
};

export function ProductSheet({ productId, onClose, onSaved, onOpenProduct }: { productId: string | null; onClose: () => void; onSaved: () => void; onOpenProduct?: (id: string) => void }) {
  const { tenantId } = useTenant();
  const q = useQuery({ queryKey: ["product", tenantId, productId], queryFn: () => api.products.get(tenantId, productId!), enabled: !!productId });

  return (
    <Dialog.Root open={!!productId} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/20 animate-fade-in" />
        <Dialog.Content
          data-devtools-host
          aria-describedby={undefined}
          // Fokus nicht automatisch ins erste Feld – SheetBody fokussiert das erste fehlerhafte Feld
          onOpenAutoFocus={(e) => e.preventDefault()}
          className="fixed z-50 right-0 top-0 h-full w-[min(640px,100vw)] bg-surface border-l border-border shadow-2xl flex flex-col animate-sheet-in outline-none"
        >
          {q.data ? (
            <SheetBody key={q.data.id + q.data.updatedAt} p={q.data} onClose={onClose} onOpenProduct={onOpenProduct} onSaved={() => { onSaved(); void q.refetch(); }} />
          ) : (
            <div className="p-6 space-y-4">
              <Dialog.Title className="sr-only">{t("Artikel wird geladen")}</Dialog.Title>
              <Skeleton className="h-6 w-2/3" />
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-48 w-full" />
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function SheetBody({ p, onSaved, onOpenProduct }: { p: ProductItem; onClose: () => void; onSaved: () => void; onOpenProduct?: (id: string) => void }) {
  const { tenantId } = useTenant();
  const initial = useMemo(() => toForm(p), [p]);
  const [form, setForm] = useState<Form>(initial);
  const [saving, setSaving] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const transfer = async () => {
    setTransferring(true);
    try {
      await api.products.transfer(tenantId, p.id);
      toast.success(`${p.sku} wird ans Lager übertragen`);
    } catch (e) {
      toast.error(e instanceof ApiError && e.status === 422 ? "Stammdaten unvollständig – bitte zuerst die markierten Felder ergänzen" : "Übertragung fehlgeschlagen");
    } finally {
      setTransferring(false);
    }
  };
  const [releasing, setReleasing] = useState(false);
  const release = async () => {
    setReleasing(true);
    try {
      await api.products.release(tenantId, [p.id]);
      toast.success(`${p.sku} wird für Logiful freigegeben`, { description: msg("Logiful kann den Artikel danach einlagern und versenden.") });
      onSaved();
    } catch (e) {
      toast.error("Freigabe nicht möglich", e instanceof ApiError ? { description: (e.body as { message?: string })?.message } : undefined);
    } finally {
      setReleasing(false);
    }
  };
  const [live, setLive] = useState<ValidationResult | null>(null);
  // Stammdaten-Prüfung des Kunden (Logiful-Einstellung) – Live-Prüfung soll dasselbe zeigen wie der Server
  const vs = useQuery({ queryKey: ["validation-settings", tenantId], queryFn: () => api.validationSettings.get(tenantId), staleTime: 5 * 60_000 });
  const liveSettings = useMemo(() => ({
    shipsOutsideEu: vs.data?.shipsOutsideEu ?? true,
    ruleOverrides: Object.fromEntries((vs.data?.rules ?? []).filter((r) => r.level !== r.defaultLevel).map((r) => [r.id, r.level === "off" ? { enabled: false } : { severity: r.level }])),
  }), [vs.data]);
  const [tab, setTab] = useState<"data" | "history">("data");
  const [confirming, setConfirming] = useState(false);
  const confirmWeight = async () => {
    setConfirming(true);
    try {
      await api.products.confirmWeight(tenantId, p.id);
      toast.success(t("Gewicht bestätigt"));
      onSaved();
    } catch {
      toast.error(t("Nicht gespeichert"));
    } finally {
      setConfirming(false);
    }
  };
  const [confirmingRule, setConfirmingRule] = useState<string | null>(null);
  const confirmPlausibility = async (ruleId: string) => {
    setConfirmingRule(ruleId);
    try {
      await api.products.confirmPlausibility(tenantId, p.id, ruleId);
      toast.success(t("Als korrekt bestätigt"), { description: t("Ändern sich Gewicht oder Maße, prüfen wir erneut.") });
      onSaved();
    } catch {
      toast.error(t("Nicht gespeichert"));
    } finally {
      setConfirmingRule(null);
    }
  };
  const refs = useRef<Partial<Record<keyof Form, HTMLInputElement | null>>>({});
  const changes = diff(initial, form);
  const dirty = Object.keys(changes).length > 0;

  // Live-Prüfung beim Tippen (Duplikatprüfungen übernimmt der Server)
  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => {
      void validateProduct(applyForm(p, form), {
        settings: liveSettings,
        backends: [{ backend: "JTL-FFN", skuMaxLength: 255, nameMaxLength: 255, requiresWeight: true, requiresDimensions: false, requiresGtin: false }],
        lookups: { countOtherProductsWithSku: async () => 0, countOtherProductsWithGtin: async () => 0, productExists: async () => true },
        // bestätigte Hinweise gelten nur für die gespeicherten Werte – beim Ändern von Gewicht/Maßen wird wieder gewarnt
        confirmed: p.plausibilityConfirmed ?? {},
      }).then((r) => !cancelled && setLive(r));
    }, 120);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [form, p, liveSettings]);

  // Server-Ergebnisse für Duplikate behalten, Rest live
  const serverOnly = p.validation.issues.filter((i) => i.ruleId.endsWith(".unique"));
  const issues = [...(live?.issues ?? p.validation.issues.filter((i) => !i.ruleId.endsWith(".unique"))), ...serverOnly];
  const errors = issues.filter((i) => i.severity === "error");
  const score = live?.completenessScore ?? p.validation.completenessScore;
  const status = errors.length ? "invalid" : issues.some((i) => i.severity === "warning") ? "warnings" : "valid";
  const fieldIssue = (k: keyof Form) => issues.find((i) => FIELD_TARGET[i.field] === k || (k === "lengthCm" && i.field === "dimensions"));

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      await api.products.patch(tenantId, p.id, changes);
      toast.success(errors.length === 0 && p.validation.status === "invalid" ? `${p.sku} ist jetzt freigegeben` : "Änderungen gespeichert", {
        description: errors.length === 0 ? "Wird ans Lager übertragen." : `${errors.length} Fehler blockieren noch die Übertragung.`,
      });
      onSaved();
    } catch (e) {
      toast.error(e instanceof ApiError && e.status === 409 ? "Nicht gespeichert" : "Speichern fehlgeschlagen", e instanceof ApiError && e.status === 409 ? { description: (e.body as { message?: string })?.message } : undefined);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        void save();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // Beim Öffnen direkt ins erste blockierende Feld springen – dorthin, wo Arbeit wartet
  useEffect(() => {
    const first = p.validation.issues.find((i) => i.severity === "error" && (FIELD_TARGET[i.field] || i.field === "dimensions"));
    const target = first ? (FIELD_TARGET[first.field] ?? "lengthCm") : undefined;
    if (target) setTimeout(() => refs.current[target]?.focus(), 240);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));
  // Schreibschutz je Feld: gesperrt = Shopify-Abgleich überschreibt nicht; lösen = sofort wieder aus Shopify übernehmen
  const [locks, setLocks] = useState<string[]>(p.lockedFields);
  useEffect(() => setLocks(p.lockedFields), [p.lockedFields]);
  const fromShopify = (p.shopLocation?.length ?? 0) > 0;
  const lockPath = (k: keyof Form) => (k === "widthCm" || k === "heightCm" ? "dimensions" : Object.entries(FIELD_TARGET).find(([path, t]) => t === k && LOCKABLE.has(path))?.[0]);
  const toggleLock = async (path: string, locked: boolean) => {
    try {
      const r = await api.products.setLock(tenantId, p.id, path, locked);
      setLocks(r.lockedFields);
      toast.success(locked ? "Vor Shopify-Abgleich geschützt" : "Wird wieder aus Shopify übernommen", { description: locked ? "Shopify überschreibt dieses Feld nicht mehr." : "Der aktuelle Wert aus Shopify wird in wenigen Sekunden übernommen." });
      if (!locked) setTimeout(onSaved, 6000);
    } catch (e) {
      toast.error("Nicht gespeichert", { description: e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "") : "" });
    }
  };
  const field = (k: keyof Form, label: string, opts: { suffix?: string; placeholder?: string; mono?: boolean; numeric?: boolean; className?: string } = {}) => {
    const iss = fieldIssue(k);
    const path = lockPath(k);
    const locked = !!path && locks.includes(path);
    return (
      <label className={cx("block", opts.className)}>
        <span className="flex items-center gap-1 text-[12px] font-medium text-fg-muted mb-1">
          {t(label)}
          {path && (locked || fromShopify) && (
            <Tip content={locked ? t("Im Portal gepflegt – der Shopify-Abgleich überschreibt dieses Feld nicht. Klicken: wieder aus Shopify übernehmen.") : t("Wird mit Shopify abgeglichen. Klicken: vor Überschreiben schützen.")}>
              <button type="button" onClick={(e) => { e.preventDefault(); void toggleLock(path, !locked); }} className={cx("size-4 grid place-items-center rounded", locked ? "text-fg-muted hover:text-fg" : "text-fg-subtle/50 hover:text-fg-muted")} aria-label={locked ? t("Schreibschutz aufheben") : t("Vor Shopify-Abgleich schützen")}>
                {locked ? <Lock className="size-3" /> : <LockOpen className="size-3" />}
              </button>
            </Tip>
          )}
        </span>
        <Input
          ref={(el) => {
            refs.current[k] = el;
          }}
          value={form[k] as string}
          onChange={(e) => set(k, e.target.value as never)}
          invalid={!!iss && iss.severity === "error"}
          suffix={opts.suffix}
          placeholder={t(opts.placeholder)}
          inputMode={opts.numeric ? "decimal" : undefined}
          className={cx(opts.mono && "font-mono text-[12.5px]")}
        />
        {iss && iss.severity !== "info" && <span className={cx("mt-1 block text-[11.5px]", iss.severity === "error" ? "text-danger" : "text-warning")}>{serverText(iss.message)}</span>}
        {/* Plausibilitätshinweis bestätigen – nur für gespeicherte Werte (sonst erst speichern) */}
        {iss && (CONFIRMABLE_RULES as readonly string[]).includes(iss.ruleId) && !dirty && (
          <Button type="button" size="sm" variant="secondary" className="mt-1.5" loading={confirmingRule === iss.ruleId} onClick={(e) => { e.preventDefault(); void confirmPlausibility(iss.ruleId); }}>
            <Check className="size-3.5" />{t("Stimmt so")}
          </Button>
        )}
      </label>
    );
  };

  const l = num(form.lengthCm), w = num(form.widthCm), h = num(form.heightCm), g = num(form.grossWeightKg);
  const volumeL = l && w && h ? (l * w * h) / 1000 : undefined;

  return (
    <>
      {/* Kopf */}
      <div className="px-6 pt-5 pb-4 border-b border-border">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <Dialog.Title className="text-[17px] font-semibold leading-6 truncate">{form.name || p.name}</Dialog.Title>
            <div className="mt-1 flex items-center gap-2 text-[12.5px] text-fg-muted">
              <span className="font-mono">{p.sku}</span>
              <span>·</span>
              <ValidationBadge status={status} />
              <ScoreBar score={score} />
            </div>
          </div>
          <SyncMenu productId={p.id} shops={p.shopLocation ?? []} />
          <Dialog.Close asChild>
            <button className="size-8 grid place-items-center rounded-lg hover:bg-surface-2 text-fg-muted" aria-label={t("Schließen")}><X className="size-4" /></button>
          </Dialog.Close>
        </div>
        <div className="mt-4 flex gap-4 text-[13px]">
          {(["data", "history"] as const).map((k) => (
            <button key={k} onClick={() => setTab(k)} className={cx("pb-1.5 -mb-[17px] border-b-2 font-medium", tab === k ? "border-coral text-fg" : "border-transparent text-fg-muted hover:text-fg")}>
              {k === "data" ? t("Stammdaten") : t("Verlauf & Synchronisation")}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {tab === "data" ? (
          <div className="px-6 py-5 space-y-6">
            {/* Prüfergebnis mit Sprung zum Feld */}
            {issues.filter((i) => i.severity !== "info").length > 0 ? (
              <section className={cx("rounded-xl border p-3", errors.length ? "border-danger/25 bg-danger-soft/50" : "border-warning/25 bg-warning-soft/50")}>
                <p className="text-[12.5px] font-semibold mb-2">
                  {errors.length ? t("{length} {v} die Übertragung ans Lager", { length: errors.length, v: errors.length === 1 ? t("Fehler blockiert") : t("Fehler blockieren") }) : t("Hinweise – Übertragung ist möglich")}
                </p>
                <ul className="space-y-1.5">
                  {issues.filter((i) => i.severity !== "info").map((i, k) => {
                    const target = FIELD_TARGET[i.field] ?? (i.field === "dimensions" ? "lengthCm" : undefined);
                    return (
                      <li key={k} className="flex items-start gap-2">
                        <SeverityIcon severity={i.severity} className="mt-0.5" />
                        <div className="min-w-0 flex-1">
                          <p className="text-[13px]">{serverText(i.message)}</p>
                          {i.hint && <p className="text-[12px] text-fg-muted">{serverText(i.hint)}</p>}
                          {i.ruleId === "gtin.unique" && !!p.sameGtin?.length && <SameGtin items={p.sameGtin} onOpen={onOpenProduct} />}
                        </div>
                        {target && (
                          <button onClick={() => refs.current[target]?.focus()} className="shrink-0 inline-flex items-center gap-1 text-[12px] font-medium text-fg hover:underline">
                            {t("Beheben")} <ArrowRight className="size-3" />
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            ) : (
              <section className="rounded-xl border border-success/25 bg-success-soft/50 p-3 flex items-center gap-2 text-[13px]">
                <CheckCircle2 className="size-4 text-success" /> {t("Stammdaten vollständig – bereit für das Lager.")}
              </section>
            )}

            {!p.bundle?.components?.length && <SalesSection productId={p.id} sku={p.sku} />}

            <Section title={t("Identifikation")}>
              {field("name", "Bezeichnung", { className: "col-span-2" })}
              {field("gtin", "EAN / GTIN", { mono: true, placeholder: "z. B. 4006381333931" })}
              {field("mpn", "Herstellerartikelnummer", { mono: true })}
              {field("manufacturer", "Hersteller", { className: "col-span-2" })}
            </Section>

            {p.weightReview && (
              <div className="col-span-2 mb-1 rounded-xl border border-warning/30 bg-warning-soft/60 px-3 py-2.5 text-[13px] flex flex-wrap items-center gap-2">
                <span className="min-w-0 flex-1">{t("Gewicht stark geändert: {from} kg → {to} kg. Bitte prüfen – Gewicht inklusive Verpackung.", { from: String(p.weightReview.from).replace(".", ","), to: String(p.weightReview.to).replace(".", ",") })}</span>
                <Button size="sm" loading={confirming} onClick={() => void confirmWeight()}>{t("Gewicht stimmt")}</Button>
              </div>
            )}
            <Section title={t("Logistik")} aside={volumeL ? `${fmtNumber(Math.round(volumeL * 100) / 100)} l${g ? ` · ${fmtNumber(Math.round((g / volumeL) * 100) / 100)} kg/l` : ""}` : undefined}>
              {/* ein Gewicht: wird als Brutto- und Nettogewicht gleich gespeichert und ans Lager gegeben */}
              {field("grossWeightKg", "Gewicht", { suffix: "kg", numeric: true, placeholder: "z. B. 0,25" })}
              <DefaultWeightHint p={p} changed={form.grossWeightKg !== initial.grossWeightKg} onReset={onSaved} />
              <div className="col-span-2 grid grid-cols-3 gap-2">
                {field("lengthCm", "Länge", { suffix: "cm", numeric: true })}
                {field("widthCm", "Breite", { suffix: "cm", numeric: true })}
                {field("heightCm", "Höhe", { suffix: "cm", numeric: true })}
              </div>
            </Section>

            <Section title={t("Zoll")} aside={t("Pflicht für Versand in Drittländer")}>
              {field("hsCode", "Zolltarifnummer", { mono: true, placeholder: "z. B. 33049900" })}
              {field("countryOfOrigin", "Ursprungsland", { placeholder: msg("z. B. DE"), mono: true })}
            </Section>

            {p.shopLocation?.some((l) => l.state === "error") && (
              <div className="col-span-2 rounded-lg border border-warning/30 bg-warning-soft/60 px-3 py-2 text-[12.5px]">
                {p.shopLocation.filter((l) => l.state === "error").map((l) => (
                  <p key={l.channelId}><span className="font-medium">{t("Nicht an der Logiful-Location in Shopify (")}{l.channelName}):</span> <span className="text-fg-muted">{serverText(l.message)}</span> <Link to={`/channels/${l.channelId}`} className="underline underline-offset-2">{t("Zum Shop")}</Link></p>
                ))}
              </div>
            )}
            <Section title={t("Handling im Lager")}>
              {p.structuralLock?.locked && <StructuralLockNote reasons={p.structuralLock.reasons.map((r) => serverText(r))} what={t("Charge, MHD und Seriennummer")} />}
              <div className="col-span-2 grid grid-cols-2 gap-2">
                {([
                  ["batchManaged", msg("Chargenpflichtig"), msg("Charge wird beim Wareneingang erfasst")],
                  ["bestBeforeManaged", msg("MHD-pflichtig"), msg("First-expired-first-out im Lager")],
                  ["serialManaged", msg("Seriennummern"), msg("Jede Einheit wird einzeln erfasst")],
                  ["fragile", msg("Zerbrechlich"), msg("Wird gesondert verpackt")],
                ] as const).map(([k, label, hint]) => {
                  const frozen = k !== "fragile" && !!p.structuralLock?.locked;
                  return (
                  <button
                    key={k}
                    onClick={() => set(k, !form[k])}
                    disabled={frozen}
                    title={frozen ? t("Gesperrt, solange das Lager Bestand, Anlieferungen oder Aufträge hat") : undefined}
                    role="switch"
                    aria-checked={form[k]}
                    className={cx("flex items-start gap-2.5 p-3 rounded-xl border text-left transition-colors disabled:opacity-60 disabled:cursor-not-allowed", form[k] ? "border-ink bg-surface-2" : "border-border hover:bg-surface-2/60")}
                  >
                    <span className={cx("mt-0.5 w-7 h-4 rounded-full p-0.5 transition-colors shrink-0", form[k] ? "bg-ink" : "bg-border-strong")}>
                      <span className={cx("block size-3 rounded-full bg-surface transition-transform", form[k] && "translate-x-3")} />
                    </span>
                    <span>
                      <span className="block text-[13px] font-medium">{t(label)}</span>
                      <span className="block text-[11.5px] text-fg-muted">{t(hint)}</span>
                    </span>
                  </button>
                  );
                })}
              </div>
            </Section>

            <Section title={t("Gefahrgut")} aside={t("z. B. Lithium-Akkus, Parfüm, Spraydosen")}>
              <button
                type="button"
                onClick={() => set("dangerous", !form.dangerous)}
                role="switch"
                aria-checked={form.dangerous}
                className={cx("col-span-2 flex items-start gap-2.5 p-3 rounded-xl border text-left transition-colors", form.dangerous ? "border-ink bg-surface-2" : "border-border hover:bg-surface-2/60")}
              >
                <span className={cx("mt-0.5 w-7 h-4 rounded-full p-0.5 transition-colors shrink-0", form.dangerous ? "bg-ink" : "bg-border-strong")}>
                  <span className={cx("block size-3 rounded-full bg-surface transition-transform", form.dangerous && "translate-x-3")} />
                </span>
                <span>
                  <span className="block text-[13px] font-medium">{t("Artikel ist Gefahrgut")}</span>
                  <span className="block text-[12px] text-fg-muted">{t("Das Lager wählt dann eine zugelassene Versandart. Geräte mit eingebautem Lithium-Akku: meist UN3481, Klasse 9.")}</span>
                </span>
              </button>
              {form.dangerous && (
                <>
                  {field("unNumber", "UN-Nummer", { mono: true, placeholder: msg("z. B. UN3481") })}
                  {field("hazardClass", "Gefahrklasse (optional)", { mono: true, placeholder: "z. B. 9" })}
                  <button
                    type="button"
                    onClick={() => set("limitedQuantity", !form.limitedQuantity)}
                    role="switch"
                    aria-checked={form.limitedQuantity}
                    className="col-span-2 flex items-center gap-2.5 text-left"
                  >
                    <span className={cx("w-7 h-4 rounded-full p-0.5 transition-colors shrink-0", form.limitedQuantity ? "bg-ink" : "bg-border-strong")}>
                      <span className={cx("block size-3 rounded-full bg-surface transition-transform", form.limitedQuantity && "translate-x-3")} />
                    </span>
                    <span className="text-[13px]"><span className="font-medium">{t("Begrenzte Menge (LQ)")}</span> <span className="text-fg-subtle">{t("· Kleinmengen mit vereinfachtem Versand")}</span></span>
                  </button>
                </>
              )}
            </Section>

            <BestBeforeSection p={p} onSaved={onSaved} />

            <PackUnitsSection key={JSON.stringify(p.packUnits ?? [])} p={p} onSaved={onSaved} />

            <BundleEditor p={p} onSaved={onSaved} />

            <ProductFbaSection productId={p.id} />
          </div>
        ) : (
          <div className="px-6 py-5 space-y-6">
            <section>
              <h3 className="text-[12px] font-semibold text-fg-muted uppercase tracking-wide mb-2">{t("Synchronisation")}</h3>
              <div className="rounded-xl border border-border divide-y divide-border">
                {(p.sync ?? []).map((s) => (
                  <div key={s.system} className="flex items-center gap-3 px-3 h-12">
                    <span className="w-24 text-[13px] font-medium">{({ shopify: "Shopify", "jtl-ffn": "Lager (FFN)", odoo: "Odoo", woocommerce: "WooCommerce", shopware: "Shopware", kaufland: "Kaufland", amazon: "Amazon" } as Record<string, string>)[s.system] ?? s.system}</span>
                    {s.state === "synced" ? <Badge tone="success">{t("synchron")}</Badge> : s.state === "blocked" ? <Badge tone="warning">{t("angehalten")}</Badge> : <Badge tone="danger">{t("Fehler")}</Badge>}
                    <span className="text-[12px] text-fg-muted truncate flex-1">{s.lastError ?? s.externalId}</span>
                    <span className="text-[12px] text-fg-subtle">{fmtRelative(s.lastSyncedAt)}</span>
                  </div>
                ))}
              </div>
            </section>
            <section>
              <h3 className="text-[12px] font-semibold text-fg-muted uppercase tracking-wide mb-3">{t("Verlauf")}</h3>
              <ol className="relative border-l border-border ml-2 space-y-4">
                {(p.history ?? []).map((h) => (
                  <li key={h.id} className="ml-4">
                    <span className="absolute -left-[5px] mt-1.5 size-2.5 rounded-full bg-surface border-2 border-fg-subtle" />
                    <p className="text-[13px]">
                      {h.action === "product.updated"
                        ? t("Geändert: {v}", { v: Object.keys(h.changes ?? {}).map((k) => ruleLabelForColumn(k)).join(", ") })
                        : h.message ?? h.action}
                    </p>
                    <p className="text-[11.5px] text-fg-subtle flex items-center gap-1">
                      <Clock className="size-3" /> {fmtDateTime(h.createdAt)} · {h.actorLabel ?? (h.actorType === "user" ? t("Portal") : h.actorType === "channel" ? t("Shop") : t("Logiful"))}
                    </p>
                  </li>
                ))}
              </ol>
            </section>
          </div>
        )}
      </div>

      {/* Speichern */}
      <div className="px-6 h-16 border-t border-border flex items-center gap-3 bg-surface">
        {dirty ? (
          <span className="text-[12.5px] text-fg-muted flex items-center gap-1.5"><CircleDot className="size-3.5 text-coral" /> {Object.keys(changes).length} {t("ungespeicherte Änderung")}{Object.keys(changes).length > 1 ? t("en") : ""}</span>
        ) : (
          <span className="text-[12.5px] text-fg-subtle flex items-center gap-1.5"><RefreshCw className="size-3.5" /> {t("Zuletzt geändert")} {fmtRelative(p.updatedAt)}</span>
        )}
        <div className="ml-auto flex items-center gap-2">
          {!dirty && p.backend?.status === "transferred" && (
            <Button variant="secondary" icon={<ShieldCheck className="size-4" />} loading={releasing} title={t("Im Lager angelegt – jetzt für Logiful freigeben")} onClick={() => void release()}>{t("Für Logiful freigeben")}</Button>
          )}
          {!dirty && p.backend?.status === "released" && (
            <span className="hidden sm:inline-flex items-center gap-1.5 text-[12.5px] text-success"><CheckCircle2 className="size-3.5" /> {t("Für Logiful freigegeben")}</span>
          )}
          {!dirty && p.backend?.status !== "released" && p.backend?.status !== "transferred" && <Button variant="ghost" icon={<Send className="size-4" />} loading={transferring} disabled={p.validation.status === "invalid"} title={p.validation.status === "invalid" ? t("Erst Stammdaten vervollständigen") : t("Stammdaten jetzt ans Lager senden")} onClick={() => void transfer()}>{t("An Lager übertragen")}</Button>}
          {dirty && <Button variant="ghost" onClick={() => setForm(initial)}>{t("Verwerfen")}</Button>}
          <Button variant="primary" disabled={!dirty} loading={saving} onClick={() => void save()}>
            {t("Speichern")} <span className="ml-1 hidden sm:inline-flex gap-0.5 opacity-60"><Kbd>⌘</Kbd><Kbd>↵</Kbd></span>
          </Button>
        </div>
      </div>
    </>
  );
}

const COLUMN_LABELS: Record<string, string> = {
  gtin: "EAN", grossWeightKg: "Gewicht", netWeightKg: "Nettogewicht", lengthCm: "Länge", widthCm: "Breite", heightCm: "Höhe",
  hsCode: "Zolltarifnummer", countryOfOrigin: "Ursprungsland", name: "Bezeichnung", batchManaged: "Charge", bestBeforeManaged: "MHD", serialManaged: "Seriennummer", fragile: "Zerbrechlich",
};
const ruleLabelForColumn = (k: string) => COLUMN_LABELS[k] ?? ruleLabel(k);

/** Absatz der letzten 90 Tage (Tagesbalken) – Grundlage der Reichweite */
function SalesSection({ productId, sku }: { productId: string; sku: string }) {
  const { tenantId } = useTenant();
  const q = useQuery({ queryKey: ["product-sales", tenantId, productId], queryFn: () => api.products.sales(tenantId, productId, 90), staleTime: 5 * 60_000 });
  if (!q.data) return null;
  const byDay = new Map(q.data.series.map((x) => [x.day, x.units]));
  const today = new Date();
  const days = Array.from({ length: 90 }, (_, i) => {
    const d = new Date(today.getTime() - (90 - i) * 86_400_000);
    const key = d.toLocaleDateString("sv-SE", { timeZone: "Europe/Berlin" });
    return { key, units: byDay.get(key) ?? 0 };
  });
  const total = days.reduce((n, d) => n + d.units, 0);
  const last30 = days.slice(-30).reduce((n, d) => n + d.units, 0);
  const max = Math.max(1, ...days.map((d) => d.units));
  return (
    <section>
      <div className="flex items-baseline mb-2.5">
        <h3 className="text-[12px] font-semibold text-fg-muted uppercase tracking-wide">{t("Absatz")}</h3>
        <span className="ml-auto text-[12px] text-fg-subtle">
          {total ? <>Ø {fmtNumber(Math.round((last30 / 30) * 10) / 10)}{t("/Tag (30 T.) ·")} {fmtNumber(total)} {t("in 90 T. ·")} </> : t("keine Verkäufe in 90 Tagen · ")}
          <Link to={`/stock?view=reach&filter=all&q=${encodeURIComponent(sku)}`} className="hover:text-fg underline-offset-2 hover:underline">{t("Reichweite")}</Link>
        </span>
      </div>
      <div className="h-14 flex items-end gap-px" role="img" aria-label={t("Absatz der letzten 90 Tage: {total} Stück", { total })}>
        {days.map((d) => (
          <Tip key={d.key} content={t("{v}: {units} Stk.", { v: new Date(d.key).toLocaleDateString(intlLocale()), units: d.units })}>
            <div className="flex-1 min-w-0 rounded-t-[2px] bg-fg-subtle/40 hover:bg-ink" style={{ height: `${Math.max(d.units ? 6 : 2, (d.units / max) * 100)}%` }} />
          </Tip>
        ))}
      </div>
    </section>
  );
}

function Section({ title, aside, children }: { title: string; aside?: ReactNode; children: ReactNode }) {
  return (
    <section>
      <div className="flex items-baseline mb-2.5">
        <h3 className="text-[12px] font-semibold text-fg-muted uppercase tracking-wide">{t(title)}</h3>
        {aside && <span className="ml-auto text-[12px] text-fg-subtle">{aside}</span>}
      </div>
      <div className="grid grid-cols-2 gap-3">{children}</div>
    </section>
  );
}

/** Hinweis: wesentliche Eigenschaften sind gesperrt, weil das Lager davon abhängt */
export function StructuralLockNote({ reasons, what }: { reasons: string[]; what: string }) {
  return (
    <div className="col-span-2 rounded-lg border border-border bg-surface-2/60 px-3 py-2 text-[12.5px]">
      <p className="font-medium flex items-center gap-1.5"><Lock className="size-3.5" /> {what} {t("derzeit nicht änderbar")}</p>
      <p className="text-fg-muted mt-0.5">{t("Im Lager:")} {reasons.join(" · ")}{t(". Änderbar, sobald kein Bestand, keine offene Anlieferung und kein offener Auftrag mehr besteht.")}</p>
    </div>
  );
}

/** Sync am Artikel: Bestand an alle verknüpften Shops oder gezielt an einen übertragen */
function SyncMenu({ productId, shops }: { productId: string; shops: NonNullable<ProductItem["shopLocation"]> }) {
  const { tenantId } = useTenant();
  const [busy, setBusy] = useState(false);
  // mehrere Listings im selben Shop = eine Anbindung; geführt, sobald ein Listing am Logiful-Standort ist
  const byShop = [...shops.reduce((m, s) => {
    const prev = m.get(s.channelId);
    m.set(s.channelId, !prev || s.state === "stocked" ? s : prev);
    return m;
  }, new Map<string, (typeof shops)[number]>()).values()];
  const push = async (channelId?: string, name?: string) => {
    setBusy(true);
    try {
      const r = await api.products.pushStock(tenantId, productId, channelId);
      toast.success(name ? `Bestand wird an ${name} übertragen` : "Bestand wird an alle Shops übertragen", { description: r.reactivated ? "Der Artikel wird dabei erneut am Logiful-Standort in Shopify aktiviert. Das Ergebnis steht gleich am Artikel." : "Das Ergebnis steht gleich unter „Verlauf & Synchronisation“." });
    } catch (e) {
      toast.error(e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "Übertragung fehlgeschlagen") : "Übertragung fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };
  const item = "flex items-start gap-2.5 rounded-lg px-2.5 py-2 text-[13px] outline-none cursor-pointer data-[highlighted]:bg-surface-2 data-[disabled]:opacity-50 data-[disabled]:cursor-default";
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button className="h-8 px-2.5 inline-flex items-center gap-1.5 rounded-lg border border-border text-[12.5px] font-medium hover:bg-surface-2 disabled:opacity-60" disabled={busy} title={t("Bestand an Shops übertragen")}>
          <RefreshCw className={cx("size-3.5", busy && "animate-spin")} /> {t("Sync")} <ChevronDown className="size-3 opacity-60" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="end" sideOffset={6} className="z-[70] w-[300px] rounded-xl border border-border bg-surface p-1 shadow-xl animate-fade-in">
          <DropdownMenu.Label className="px-2.5 pt-1.5 pb-1 text-[11.5px] font-medium text-fg-subtle">{t("Bestand übertragen")}</DropdownMenu.Label>
          <DropdownMenu.Item disabled={!byShop.length} onSelect={() => void push()} className={item}>
            <UploadCloud className="size-4 mt-0.5 text-fg-muted shrink-0" />
            <span><span className="block font-medium">{t("An alle Shops übertragen")}</span><span className="block text-[11.5px] text-fg-subtle">{byShop.length ? t("{length} verknüpfte Anbindung{v}", { length: byShop.length, v: byShop.length === 1 ? "" : t("en") }) : t("Mit keinem Shop verknüpft")}</span></span>
          </DropdownMenu.Item>
          {byShop.length > 0 && <DropdownMenu.Separator className="my-1 h-px bg-border" />}
          {byShop.map((s) => (
            <DropdownMenu.Item key={s.channelId} onSelect={() => void push(s.channelId, s.channelName)} className={item} title={s.state === "error" ? (s.message ?? undefined) : undefined}>
              <ChannelLogo kind="shopify" size="sm" />
              <span className="min-w-0 flex-1">
                <span className="block font-medium truncate">{s.channelName}</span>
                {s.state === "stocked" ? (
                  <span className="block text-[11.5px] text-fg-subtle truncate">{s.shop}</span>
                ) : (
                  <span className="block text-[11.5px] text-warning">{s.state === "error" ? t("Aktivierung am Logiful-Standort fehlgeschlagen") : t("Noch nicht am Logiful-Standort geführt")} {t("– Klick versucht es erneut")}</span>
                )}
              </span>
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

/** Hinweis „Standardgewicht des Kunden“ bzw. „zurück auf Standardgewicht“ neben dem Gewicht */
function DefaultWeightHint({ p, changed, onReset }: { p: ProductItem; changed: boolean; onReset: () => void }) {
  const { tenantId } = useTenant();
  const [busy, setBusy] = useState(false);
  const kg = p.tenantDefaultWeightKg;
  const show = (v: number) => String(v).replace(".", ",");
  if (p.weightIsDefault && !changed)
    return (
      <div className="self-end pb-2 text-[12px] text-fg-muted">
        <span className="inline-flex items-center rounded-full bg-info-soft px-2 py-0.5 font-medium text-info">{t("Standardgewicht")}</span>
        <span className="block mt-1">{t("Vom Kunden hinterlegtes Standardgewicht – hier überschreiben, wenn der Artikel abweicht.")}</span>
      </div>
    );
  if (!kg || changed) return <div />;
  const reset = async () => {
    setBusy(true);
    try {
      await api.products.useDefaultWeight(tenantId, p.id);
      toast.success(t("Standardgewicht {kg} kg übernommen", { kg: show(kg) }));
      onReset();
    } catch {
      toast.error(t("Nicht gespeichert"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="self-end pb-2">
      <button type="button" disabled={busy} onClick={() => void reset()} className="text-[12px] font-medium text-fg-muted underline underline-offset-2 hover:text-fg disabled:opacity-50">
        {t("Zurück auf Standardgewicht ({kg} kg)", { kg: show(kg) })}
      </button>
    </div>
  );
}
